import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAuthStore from 'store/useAuthStore';
import { Button, Tooltip } from 'flowbite-react';
import { Menu, MenuHandler, MenuItem, MenuList } from 'components/ui';
import { useNavigate } from 'react-router';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
  PowerIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SaldoVendorModal from './components/saldo-vendor';
import { buildMenuGroups } from 'route/menu';
import { createMenuAccessChecker } from 'helpers/menuAccess';
import { getSearchableMenus, searchMenus } from 'helpers/menuSearch';
import packageJson from '../../../package.json';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { ENV } from 'config/env';

const Header = ({ show, toggle, request }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const authClear = useAuthStore((s) => s.authClear);
  const [isInFullScreen, setIsInFullScreen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuList = useMemo(
    () => getSearchableMenus(buildMenuGroups(), createMenuAccessChecker(user)),
    [user]
  );
  const searchResults = useMemo(
    () =>
      searchMenus(menuList, searchQuery).reduce((groups, item) => {
        const category = item.location.split(' / ')[0];
        (groups[category] ||= []).push(item);
        return groups;
      }, {}),
    [menuList, searchQuery]
  );
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isSearchVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchVisible]);

  const isInFullScreenFn = () => {
    return (
      (document.fullscreenElement && true) ||
      (document.webkitFullscreenElement && true) ||
      (document.mozFullScreenElement && true) ||
      (document.msFullscreenElement && true)
    );
  };

  const toggleFullScreen = () => {
    const isFS = isInFullScreenFn();

    const docElm = document.documentElement;
    if (!isFS) {
      if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
      } else if (docElm.mozRequestFullScreen) {
        docElm.mozRequestFullScreen();
      } else if (docElm.webkitRequestFullScreen) {
        docElm.webkitRequestFullScreen();
      } else if (docElm.msRequestFullscreen) {
        docElm.msRequestfullscreen();
      }
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    setIsInFullScreen(!isFS);
  };

  const handleLogout = async () => {
    const currentToken = localStorage.getItem('Pulsalink-Admin-Auth-Key');

    if (currentToken) {
      try {
        await fetch(ENV.API_HOST + 'logout-device', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          body: new URLSearchParams({ api_token: currentToken }).toString(),
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
    }

    authClear('manual');
    navigate('user/login');
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const openSearchResult = (item) => {
    setIsSearchVisible(false);
    setSearchQuery('');
    navigate(item.to);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchQuery('');
        setIsSearchVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className='relative z-40 flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 text-black shadow-sm sm:px-6'>
      <style>
        {`
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slide-down {
            animation: slideDown 0.3s ease-out forwards;
          }
        `}
      </style>
      <div className='flex min-w-0 flex-1 items-center'>
        <button
          type='button'
          className='mr-3 inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          onClick={toggle}
          aria-controls='primary-sidebar'
          aria-expanded={show}
          aria-label={show ? 'Tutup navigasi' : 'Buka navigasi'}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth='1.5'
            stroke='currentColor'
            className='h-6 w-6'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
              stroke='#8f8f8f'
            />
          </svg>
        </button>
        <div ref={searchRef} className='relative flex min-w-0 items-center'>
          {!isSearchVisible && (
            <button
              type='button'
              aria-label='Buka pencarian menu'
              className='cursor-pointer text-gray-500 hover:text-gray-700 p-2 rounded table-row-hover transition-colors'
              onClick={() => setIsSearchVisible(true)}
            >
              <MagnifyingGlassIcon className='h-5 w-5' aria-hidden='true' />
            </button>
          )}
          <div
            className={`flex items-center transition-all duration-300 ease-in-out ${
              isSearchVisible
                ? 'min-w-0 w-full opacity-100 translate-x-0 md:w-96'
                : 'w-0 -translate-x-4 overflow-hidden opacity-0'
            }`}
          >
            <div className='relative w-full'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              </div>
              <input
                ref={searchInputRef}
                placeholder='Cari menu...'
                aria-label='Cari menu'
                className='w-full pl-10 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm text-sm bg-gray-50 focus:bg-white transition-colors'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                role='searchbox'
                aria-autocomplete='list'
                aria-controls='search-results'
                autoComplete='off'
                maxLength={120}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsSearchVisible(false);
                    setSearchQuery('');
                  } else if (event.key === 'Enter') {
                    const firstResult = Object.values(searchResults)[0]?.[0];
                    if (firstResult) {
                      event.preventDefault();
                      openSearchResult(firstResult);
                    }
                  }
                }}
              />
              <button
                type='button'
                aria-label='Tutup pencarian'
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors'
                onClick={() => {
                  setIsSearchVisible(false);
                  setSearchQuery('');
                }}
              >
                <XMarkIcon className='h-4 w-4' aria-hidden='true' />
              </button>

              {isSearchVisible && searchQuery && Object.keys(searchResults).length > 0 && (
                <div
                  id='search-results'
                  role='listbox'
                  aria-label='Hasil pencarian menu'
                  className='absolute left-0 mt-4 w-full md:w-[500px] bg-white border border-gray-100 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] z-50 max-h-[80vh] overflow-y-auto overflow-x-hidden animate-slide-down'
                >
                  {Object.entries(searchResults).map(([category, items]) => (
                    <div key={category} className='mb-2'>
                      <div className='px-4 py-3 bg-gray-50/80 backdrop-blur-sm text-xs font-bold text-primary uppercase tracking-wider sticky top-0 z-10 border-b border-gray-100 flex items-center gap-2'>
                        {category}
                      </div>
                      <div className='py-1'>
                        {items.map((item) => (
                          <button
                            key={item.id}
                            type='button'
                            role='option'
                            aria-selected={false}
                            className='w-full px-4 py-3 text-left hover:bg-blue-50 cursor-pointer transition-all duration-200 border-b border-gray-50 last:border-0 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
                            onClick={() => openSearchResult(item)}
                          >
                            <div className='flex items-center justify-between'>
                              <div className='flex flex-col'>
                                <span className='text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors'>
                                  {item.label}
                                </span>
                                <div className='flex items-center text-xs text-gray-400 group-hover:text-blue-400 mt-0.5'>
                                  <span>{item.location}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isSearchVisible && searchQuery && Object.keys(searchResults).length === 0 && (
                <div className='absolute left-0 mt-4 w-full bg-white border border-gray-100 rounded-2xl shadow-xl z-50 p-8 text-center animate-slide-down'>
                  <div className='flex justify-center mb-3'>
                    <MagnifyingGlassIcon className='h-12 w-12 text-gray-300' />
                  </div>
                  <p className='text-base font-medium text-gray-900'>Menu tidak ditemukan</p>
                  <p className='text-sm text-gray-500 mt-1'>
                    Coba kata kunci lain untuk mencari menu
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='flex shrink-0 flex-row items-center justify-end gap-x-1.5'>
        <div className='mr-2 hidden items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 md:flex md:mr-3'>
          v{packageJson.version}
        </div>
        <Tooltip content='Saldo Vendor' placement='bottom'>
          <div>
            <Button
              color='unstyled'
              className='hidden md:mr-4 md:inline-flex items-end md:items-center py-2 bg-white/10 md:bg-primary shadow-none hover:shadow-none md:hover:shadow-lg md:text-white text-primary text-sm font-medium rounded-md'
              onClick={handleModalOpen}
            >
              <CurrencyDollarIcon className='w-5' />
            </Button>
            <button
              type='button'
              aria-label='Lihat saldo vendor'
              className='flex md:hidden'
              onClick={handleModalOpen}
            >
              <CurrencyDollarIcon className='w-8 text-primary' />
            </button>
          </div>
        </Tooltip>

        <button
          className='hidden md:inline-flex items-center justify-center h-full bg-transparent md:mr-2 p-1.5 rounded table-row-hover transition-colors'
          type='button'
          id='fullScreenButton'
          onClick={toggleFullScreen}
          aria-label={isInFullScreen ? 'Keluar dari layar penuh' : 'Aktifkan layar penuh'}
        >
          {isInFullScreen ? (
            <ArrowsPointingInIcon className='h-5 w-5 text-gray-400 hover:text-primary' />
          ) : (
            <ArrowsPointingOutIcon className='h-5 w-5 text-gray-400 hover:text-primary' />
          )}
        </button>

        <div className='flex border-l border-slate-200 pl-3 sm:pl-4'>
          <Menu placement='bottom-end'>
            <MenuHandler>
              <button
                type='button'
                aria-label={`Menu akun ${user?.user_name || 'admin'}`}
                className='group flex items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
              >
                <div className='flex items-center justify-center'>
                  {!user?.user_photo || user?.user_photo === '' || imageError ? (
                    <UserCircleIcon className='size-8 rounded-full bg-primary text-white' />
                  ) : user?.user_photo && user?.user_photo !== '' ? (
                    <img
                      alt={user?.user_name || 'Default Profile'}
                      width={35}
                      className='size-8 rounded-full object-cover'
                      src={ENV.IMG_HOST + user?.user_photo}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <UserCircleIcon className='size-8 bg-primary text-white rounded-full' />
                  )}
                </div>
                <ChevronDownIcon
                  className='h-4 w-4 text-gray-400 group-hover:text-gray-500'
                  aria-hidden='true'
                />
              </button>
            </MenuHandler>
            <MenuList className='p-0! overflow-hidden bg-transparent! border-0! shadow-xl'>
              <MenuItem className='p-0! cursor-default hover:bg-transparent!'>
                <div className="bg-[url('/src/assets/img/login/bg-login.jpg')] flex bg-cover relative text-white">
                  <div className='flex items-center justify-between py-6 px-4 bg-primary/65 space-x-8'>
                    <div className='flex items-center justify-center mr-2'>
                      <div className='flex items-center justify-center mr-2'>
                        {!user?.user_photo || user?.user_photo === '' || imageError ? (
                          <UserCircleIcon className='size-8 bg-white text-primary rounded-full' />
                        ) : user?.user_photo && user?.user_photo !== '' ? (
                          <img
                            alt={user?.user_name || 'Default Profile'}
                            width={35}
                            className='rounded-full'
                            src={ENV.IMG_HOST + user?.user_photo}
                            onError={() => setImageError(true)}
                          />
                        ) : (
                          <UserCircleIcon className='size-8 bg-white text-primary rounded-full' />
                        )}
                      </div>
                      <div className='flex flex-col items-start justify-center'>
                        <div className='text-sm font-semibold text-white'>{user?.user_name}</div>
                        <div className='text-xs font-medium text-white'>
                          {user?.user_level} PulsaLink
                        </div>
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={() => {
                        handleLogout();
                      }}
                      className='flex items-center justify-center rounded-xl bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-500 hover:shadow-md'
                    >
                      <PowerIcon className='h-4 w-4 mr-1' />
                      Logout
                    </button>
                  </div>
                </div>
              </MenuItem>
            </MenuList>
          </Menu>
          <div className='hidden md:block'>
            <div className='flex flex-col items-start justify-center'>
              <div className='text-sm font-semibold text-gray-900'>{user?.user_name}</div>
              <div className='text-xs font-medium text-gray-500'>{user?.user_level} PulsaLink</div>
            </div>
          </div>
        </div>
      </div>
      <SaldoVendorModal open={modalOpen} request={request} handleClose={handleModalClose} />
    </header>
  );
};

export default Header;
